import { useMemo, useState } from "react";
import { MRT_ColumnDef } from "material-react-table";
import VirtualizedTable from "../components/VirtualizeTable";
import PlansDelete from "./PlansDelete";
import PlansUpdate from "./PlansUpdate";
import { formatPrice, formatRecurrence } from "../helpers/format";
import { IconEdit, IconTrash } from "@tabler/icons-react";

const PlansTableComponent = ({
  data,
  refetch,
}: {
  data: Array<{
    id: string;
    name: string;
    status: boolean;
    value: string;
    recurrence: string;
    description?: string;
    installments?: string;
    fights: { fightId: string }[];
    free: boolean;
    paymentsType: string;
  }>;
  refetch: () => void;
}) => {
  const [deleteAlert, setDeleteAlert] = useState(false);
  const [edit, setEdit] = useState(false);
  const [value, setValue] = useState(data[0] || {});
  const [id, setId] = useState("");

  const columns = useMemo<MRT_ColumnDef<(typeof data)[0]>[]>(() => {
    return [
      {
        accessorKey: "name",
        header: "Nome",
      },
      {
        accessorKey: "description",
        header: "Descrição",
      },
      {
        accessorKey: "value",
        header: "Valor",
        Cell: ({ cell }) => formatPrice(cell.getValue()),
      },
      {
        accessorKey: "installments",
        header: "Parcelas",
        Cell: ({ cell }) => {
          const value = cell.getValue();
          const price = cell.row.original.value;
          return value
            ? `${value} x ${formatPrice(Number(price) / Number(value))}`
            : "1x";
        },
      },
      {
        accessorKey: "recurrence",
        header: "Recorrência",
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return typeof value === "string" ? formatRecurrence(value) : "-";
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({ cell }) => {
          const status = cell.getValue();
          const label = status ? "Ativo" : "Inativo";
          const color = status ? "#2e7d32" : "#d32f2f";
          return (
            <div
              style={{
                padding: "4px 12px",
                borderRadius: "12px",
                backgroundColor: color,
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.75rem",
                textAlign: "center",
                display: "inline-block",
                minWidth: "100px",
              }}
            >
              {label}
            </div>
          );
        },
      },
      {
        id: "edit",
        header: "Editar",
        size: 70,
        Cell: ({ row }: any) => (
          <IconEdit
            onClick={() => {
              setEdit(true);
              setValue(row.original);
            }}
            style={{ cursor: "pointer", color: "#FFF" }}
          />
        ),
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
        enableSorting: false,
        enableColumnActions: false,
      },
      {
        id: "delete",
        header: "Excluir",
        size: 70,
        Cell: ({ row }: any) => (
          <IconTrash
            onClick={() => {
              setId(row.original.id);
              setDeleteAlert(true);
            }}
            style={{ cursor: "pointer", color: "#d32f2f" }}
          />
        ),
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
        enableSorting: false,
        enableColumnActions: false,
      },
    ];
  }, [data]);

  return (
    <>
      <VirtualizedTable
        data={data}
        columns={columns}
        fetchNextPage={() => Promise.resolve()}
        isLoading={false}
      />

      <PlansDelete
        open={deleteAlert}
        onClose={() => setDeleteAlert(false)}
        id={id}
        onAction={refetch}
      />
      {edit && (
        <PlansUpdate
          open={edit}
          onClose={() => setEdit(false)}
          item={value}
          refetch={refetch}
        />
      )}
    </>
  );
};

export default PlansTableComponent;
